import React from 'react';
import { useTranslation } from 'react-i18next';
import { Layers } from 'lucide-react';
import { Card } from '../design/ui';
import { displayLocale } from '../utils/dates';
import { getStatusLabel } from '../constants/ticketStatuses';
import './DashboardPipeline.css';

// Every open ticket by stage of its case — GET /api/tickets/dashboard-insights
// `pipeline` (Kevin 29/10: the status tiles covered 4 statuses out of 20).
// One row per stage the API returned, in the order of a case; the bar is the
// stage's share of the largest stage, its washed part the « dormant » tickets
// (status unchanged for more than `dormant_after_days`). A row filters the
// list on the stage's statuses; its dormant count, on those dormant tickets.

const STAGE_DEFAULTS = {
  team: ['Votre équipe', 'Nouveaux, à traiter, affectés : la prochaine action est chez vous.'],
  carrier: ['Transporteur', 'En attente d’une réponse du transporteur ou litige en cours.'],
  client: ['Client', 'En attente d’une réponse ou de pièces du client.'],
  warehouse: ['Entrepôt', 'En attente d’une réponse de l’entrepôt.'],
  compensation: ['Indemnisation', 'À indemniser, indemnisation en cours ou facturée.'],
  npec: ['NPEC', 'Non pris en charge, en attente de clôture.'],
  return: ['Retour', 'Colis en cours de retour.'],
  ai: ['IA', 'Traités automatiquement par l’agent IA.'],
  other: ['Autres statuts', 'Statuts hors étapes : leur nom est listé.'],
};

export const stageLabel = (t, key) => t(`dashPipelineUi.stage.${key}`, (STAGE_DEFAULTS[key] || [key])[0]);
const stageHint = (t, key) => t(`dashPipelineUi.stageHint.${key}`, (STAGE_DEFAULTS[key] || ['', ''])[1]);

export const stageStatuses = (stage) => (stage.statuses || []).map((row) => row.status);

const sameSet = (a, b) => a.length === b.length && a.every((value) => b.includes(value));

const DashboardPipeline = ({ pipeline, activeStatuses = [], dormantActive = false, onSelect }) => {
  const { t, i18n } = useTranslation();
  const locale = displayLocale(i18n.language);
  // A plain no-break space between thousands: the narrow one French formatting
  // uses nearly vanishes in small text (same choice as DashboardInsights).
  const number = (value) => new Intl.NumberFormat(locale).format(value).replace(/\u202f/g, '\u00a0');

  if (pipeline === undefined) {
    return (
      <div className="u-skeleton dash-pipe__skeleton" role="status" aria-busy="true"
        aria-label={t('dashPipelineUi.loading', 'Chargement des étapes')} />
    );
  }
  if (!pipeline || !Array.isArray(pipeline.stages) || pipeline.stages.length === 0) return null;

  const days = pipeline.dormant_after_days;
  const largest = Math.max(...pipeline.stages.map((stage) => stage.count), 1);
  const allStatuses = pipeline.stages.flatMap(stageStatuses);
  const select = (statuses, dormant) => { if (onSelect) onSelect({ statuses, dormant }); };
  const title = t('dashPipelineUi.title', 'Tickets ouverts par étape');

  const description = (
    <>
      {t('dashPipelineUi.total', '{{formatted}} tickets ouverts', {
        count: pipeline.total, formatted: number(pipeline.total),
      })}
      {pipeline.dormant_total > 0 && (
        <>
          {' · '}
          <button
            type="button"
            className="u-link dash-pipe__link"
            aria-pressed={dormantActive && sameSet(activeStatuses, allStatuses)}
            onClick={() => select(allStatuses, true)}
          >
            {t('dashPipelineUi.dormantTotal', '{{formatted}} sans changement de statut depuis plus de {{days}} jours', {
              count: pipeline.dormant_total, formatted: number(pipeline.dormant_total), days,
            })}
          </button>
        </>
      )}
    </>
  );

  return (
    <Card className="dash-pipe" icon={Layers} title={title} description={description}>
      <div className="dash-pipe__table" role="table" aria-label={title}>
        <div className="dash-pipe__row dash-pipe__row--head" role="row">
          <span role="columnheader" className="u-overline">{t('dashPipelineUi.colStage', 'Étape')}</span>
          <span role="columnheader" className="u-overline dash-pipe__num">{t('dashPipelineUi.colCount', 'Tickets')}</span>
          <span role="columnheader" className="u-overline dash-pipe__num">
            {t('dashPipelineUi.colDormant', 'Dormants > {{days}} j', { days })}
          </span>
          <span role="columnheader" className="u-overline dash-pipe__num">
            {t('dashPipelineUi.colMedian', 'Temps médian')}
          </span>
        </div>
        {pipeline.stages.map((stage) => {
          const statuses = stageStatuses(stage);
          const active = sameSet(activeStatuses, statuses);
          const label = stageLabel(t, stage.key);
          const detail = (stage.statuses || [])
            .map((row) => `${getStatusLabel(t, row.status)} ${number(row.count)}`)
            .join(' · ');
          return (
            <div key={stage.key} role="row" className={`dash-pipe__row${active ? ' is-active' : ''}`}>
              <span role="cell" className="dash-pipe__stage">
                <button
                  type="button"
                  className="dash-pipe__stage-btn"
                  aria-pressed={active && !dormantActive}
                  onClick={() => select(active && !dormantActive ? [] : statuses, false)}
                  title={stageHint(t, stage.key)}
                >
                  <span className="dash-pipe__stage-name">{label}</span>
                  <span className="dash-pipe__stage-detail" title={detail}>{detail}</span>
                </button>
                <span
                  className="dash-pipe__bar"
                  title={t('dashPipelineUi.barHint', '{{dormant}} sur {{total}} sans changement de statut depuis plus de {{days}} jours', {
                    dormant: number(stage.dormant), total: number(stage.count), days,
                  })}
                >
                  <span className="dash-pipe__bar-active" style={{ width: `${(stage.active / largest) * 100}%` }} />
                  <span className="dash-pipe__bar-dormant" style={{ width: `${(stage.dormant / largest) * 100}%` }} />
                </span>
              </span>
              <span role="cell" className="dash-pipe__num dash-pipe__count">{number(stage.count)}</span>
              <span role="cell" className="dash-pipe__num">
                {stage.dormant > 0 ? (
                  <button
                    type="button"
                    className="u-link dash-pipe__link"
                    aria-pressed={active && dormantActive}
                    onClick={() => select(statuses, true)}
                    aria-label={t('dashPipelineUi.dormantRow', '{{label}} : {{formatted}} tickets sans changement de statut depuis plus de {{days}} jours', {
                      label, count: stage.dormant, formatted: number(stage.dormant), days,
                    })}
                  >
                    {number(stage.dormant)}
                  </button>
                ) : <span className="dash-pipe__muted">0</span>}
              </span>
              <span role="cell" className="dash-pipe__num">
                {typeof stage.median_days_in_status === 'number'
                  ? t('dashPipelineUi.days', '{{count}} j', { count: Math.round(stage.median_days_in_status) })
                  : <span className="dash-pipe__muted">—</span>}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default DashboardPipeline;
